import type { DateTime } from "luxon";
import { DailyPrintoutGroupingOption } from "../../../utils/models";
import { compareSections, type SectionDirections, type SectionSortKeys } from "../sort/groupSort";
import type { DailyPrintoutGrouping, PrintoutColumn, PrintoutColumnType, ScheduleRow, SectionPlan, ShiftTimeAnchor } from "../types";
import { groupKeyFor } from "./groupKey";
import { groupRowsByCanonicalShiftTime } from "./shiftTimeCanonical";
import { groupRowsByDepartmentAnchors } from "./shiftTimeDepartmentAnchors";

interface GroupRowsArgs {
	rows: ScheduleRow[];
	printoutGroupBy: DailyPrintoutGrouping;
	jobManagementEnabled: boolean;
	customStringOrderMap: Map<string, number> | null | undefined;
	/** User-configured `sortBy` — direction is applied per grouping level (mapping §5.7). */
	sortBy?: PrintoutColumn[];
	/** When on (and Shift Time grouping is active), snap shift-time groups to department popular windows. */
	alignGroupTimesToPopular?: boolean;
	/** Department popular shift windows used as anchors when `alignGroupTimesToPopular` is on. */
	shiftTimeAnchors?: ShiftTimeAnchor[];
	/** The printout day — anchors (times-of-day) are materialized onto this day. */
	day?: DateTime;
	/** Department timezone for the anchor/row minute-of-day conversion. */
	timezone?: string;
}

/** Map a grouping kind to the printout column type whose `sortBy` direction governs that level. */
const GROUPING_TO_COLUMN: Partial<Record<DailyPrintoutGroupingOption, PrintoutColumnType>> = {
	[DailyPrintoutGroupingOption.LOCATION]: "location",
	[DailyPrintoutGroupingOption.ROLE]: "role",
	[DailyPrintoutGroupingOption.FLOOR]: "floor",
	[DailyPrintoutGroupingOption.START_TIME]: "shiftTime",
};

/**
 * One in-progress group during multi-level nesting.
 *
 * The `levelKeys` mirror `levelSortKeys` until the final section sort, where shift-time levels
 * get upgraded to the canonical DateTime. Track both so the composite display key (string-joined)
 * stays stable while the sort respects the canonical bucket's anchor time (mapping §5.7).
 */
interface PartialSection {
	levelKeys: string[];
	levelSortKeys: SectionSortKeys;
	rows: ScheduleRow[];
}

/**
 * Apply a single grouping kind to a list of partial sections, producing one extra level of nesting.
 *
 * For `START_TIME`, this is where §6.3 canonicalization fires: each parent bucket's rows are
 * subdivided into canonical shift-time groups, and the canonical bucket's anchor DateTime is
 * recorded into `levelSortKeys` (mapping §5.7).
 */
/** Inputs for the popular-times anchor grouping mode, forwarded to the `START_TIME` branch. */
interface AlignShiftTimeOptions {
	alignGroupTimesToPopular?: boolean;
	shiftTimeAnchors?: ShiftTimeAnchor[];
	day?: DateTime;
	timezone?: string;
}

const applyOneGroupingLevel = (
	parents: PartialSection[],
	groupingOption: DailyPrintoutGroupingOption,
	jobManagementEnabled: boolean,
	alignOptions: AlignShiftTimeOptions,
): PartialSection[] => {
	const next: PartialSection[] = [];
	const { alignGroupTimesToPopular, shiftTimeAnchors, day, timezone } = alignOptions;
	const canUseDepartmentAnchors = !!alignGroupTimesToPopular && !!shiftTimeAnchors?.length && !!day && !!timezone;

	for (const parent of parents) {
		if (groupingOption === DailyPrintoutGroupingOption.START_TIME) {
			/* When the user opts in (and anchors are available), snap groups to the department's popular
			 * shift windows; otherwise derive anchors from per-row popularity. Both return the same shape.*/
			const { groups, canonicalStart } = canUseDepartmentAnchors
				? groupRowsByDepartmentAnchors(parent.rows, shiftTimeAnchors, day, timezone)
				: groupRowsByCanonicalShiftTime(parent.rows);
			for (const [canonicalKey, rows] of groups) {
				const anchorStart: DateTime | undefined = canonicalStart.get(canonicalKey);
				next.push({
					levelKeys: [...parent.levelKeys, canonicalKey],
					// Use the canonical DateTime for sort (mapping §5.7); fall back to the key string if absent.
					levelSortKeys: [...parent.levelSortKeys, anchorStart ?? canonicalKey],
					rows,
				});
			}
			continue;
		}

		const buckets = new Map<string, ScheduleRow[]>();
		for (const row of parent.rows) {
			const key = groupKeyFor({ row, groupingOption, jobManagementEnabled });
			const existing = buckets.get(key);
			if (existing) existing.push(row);
			else buckets.set(key, [row]);
		}
		for (const [key, rows] of buckets) {
			next.push({
				levelKeys: [...parent.levelKeys, key],
				levelSortKeys: [...parent.levelSortKeys, key],
				rows,
			});
		}
	}
	return next;
};

/**
 * Phase 3 — partition rows into ordered sections (mapping §4 phase 3 / §6.*).
 *
 * Multi-level nesting composes section labels with `" | "` (mapping §6.4 — flat visual structure).
 * Sections are sorted via the shared `compareSections` (mapping §5.7); within each section the
 * input row order is preserved by Array stability.
 */
export const groupRows = (args: GroupRowsArgs): SectionPlan[] => {
	const { rows, printoutGroupBy, jobManagementEnabled, customStringOrderMap, sortBy, alignGroupTimesToPopular, shiftTimeAnchors, day, timezone } = args;

	const activeGroupings = printoutGroupBy.filter((g) => g !== DailyPrintoutGroupingOption.NONE);
	if (activeGroupings.length === 0) {
		return [{ groupingKey: "all", rows }];
	}

	const alignOptions: AlignShiftTimeOptions = { alignGroupTimesToPopular, shiftTimeAnchors, day, timezone };
	let partials: PartialSection[] = [{ levelKeys: [], levelSortKeys: [], rows }];
	for (const groupingOption of activeGroupings) {
		partials = applyOneGroupingLevel(partials, groupingOption, jobManagementEnabled, alignOptions);
	}

	// Per-level direction multipliers: when the user has a `sortBy` entry whose column matches the
	// grouping level, honor its direction. Otherwise default to asc. Mirrors legacy
	// `compareByGroupingOption` (downloadDetailedDayView.tsx:2618).
	const directions: SectionDirections = activeGroupings.map((g) => {
		const colType = GROUPING_TO_COLUMN[g];
		if (!colType) return 1;
		const entry = sortBy?.find((s) => s.enabled && s.type === colType);
		return entry?.sortDirection === "desc" ? -1 : 1;
	});

	const sectionComparator = compareSections(customStringOrderMap, directions);
	partials.sort((a, b) => sectionComparator(a.levelSortKeys, b.levelSortKeys));

	return partials.map<SectionPlan>((p) => ({
		groupingKey: p.levelKeys.join(" | "),
		rows: p.rows,
	}));
};
